import React, { createContext, PropsWithChildren, useMemo } from 'react';
import axios, { AxiosError, AxiosInstance } from 'axios';
import history from 'infrastructure/history';
import {
  calculateExpiryTime,
  getAccessToken,
  getAccessTokenExpiryTime,
  removeAccessToken,
  setAccessToken,
} from 'infrastructure/security/accessTokenManager';
import canUseEnvTenant from 'infrastructure/currentTenant/canUseEnvTenant';
import {
  buildLoginPath,
  getCurrentLoginReturnPath,
  readLoginRedirect,
} from 'infrastructure/security/loginRedirect';
import setTenantApiKeyParam from './setTenantApiKeyParam';

const handleLogout = () => {
  removeAccessToken();
  const currentPath = history.location.pathname;
  const currentSearch = history.location.search;

  // List of paths that shouldn't be used as redirect targets
  const authPaths = [
    '/login',
    '/forgot-password',
    '/reset-password',
    '/password-reset-confirmation',
  ];
  const isAuthPath = authPaths.some(path => currentPath.startsWith(path));

  if (isAuthPath) {
    // If we're on an auth page, preserve any existing redirectUrl
    const existingRedirectUrl = readLoginRedirect(currentSearch);
    if (existingRedirectUrl) {
      history.push(buildLoginPath(existingRedirectUrl));
    } else {
      history.push('/login');
    }
  } else {
    history.push(
      buildLoginPath(getCurrentLoginReturnPath(currentPath, currentSearch))
    );
  }
};

export const AxiosContext = createContext({} as AxiosInstance);

const AxiosContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const axiosInstance = useMemo(() => {
    const instance = axios.create();

    instance.interceptors.response.use(
      response => {
        const wasRefreshRequest =
          response.config.url?.includes('/auth/refresh');
        const wasLoginLogoutRequest =
          response.config.url?.includes('/auth/login') ||
          response.config.url?.includes('/auth/logout');
        const bearerToken = getAccessToken();
        const isTokenExpring =
          new Date(getAccessTokenExpiryTime()) < new Date();

        if (
          !wasRefreshRequest &&
          !wasLoginLogoutRequest &&
          bearerToken &&
          isTokenExpring
        ) {
          axios
            .post(
              new URL('/api/auth/refresh', process.env.API_URL).href,
              {},
              {
                headers: {
                  Authorization: `Bearer ${bearerToken}`,
                  'Content-type': 'application/json; charset=UTF-8',
                },
              }
            )
            .then(tokenRefreshResponse => {
              const expiryTimeWithBuffer = calculateExpiryTime(
                tokenRefreshResponse.data.expires_in
              );

              setAccessToken(
                JSON.stringify({
                  ...tokenRefreshResponse.data,
                  expires_at: expiryTimeWithBuffer,
                })
              );
            });
        }
        return response;
      },
      (error: AxiosError) => Promise.reject(error)
    );

    instance.interceptors.request.use(request => {
      const token = getAccessToken();

      if (!request.headers) {
        request.headers = {};
      }
      if (token) {
        request.headers.Authorization = `Bearer ${token}`;
      } else {
        delete request.headers.Authorization;
      }

      // Ensure params object exists
      if (!request.params) {
        request.params = {};
      }

      if (canUseEnvTenant && process.env.CURRENT_TENANT_API_KEY) {
        request.params = setTenantApiKeyParam(
          request.params,
          process.env.CURRENT_TENANT_API_KEY
        );
      }

      return request;
    });

    instance.interceptors.response.use(
      response => response,
      error => {
        if (
          error?.response?.status === 401 &&
          !error?.config?.skipAuthRedirect
        ) {
          handleLogout();
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, []);

  return (
    <AxiosContext.Provider value={axiosInstance}>
      {children}
    </AxiosContext.Provider>
  );
};

export default AxiosContextProvider;
